/**
 * CommissionsPage — Rémunérations & commissions (P2 batch 3).
 * Vue unifiée : dashboard mensuel + liste des commissions + saisie manuelle.
 * Rôle admin/manager → scope agence par défaut ; agent → scope mine.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    TrendingUp, Plus, Download, FileText, Loader2, Calculator, Trash2,
    CheckCircle2, Clock, Settings2, Euro,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import ReseauLayout from '../../components/reseau/ReseauLayout';

const STATUS_COLORS = {
    pending: { label: 'En attente', bg: 'rgba(234,179,8,0.12)', color: '#a16207' },
    validated: { label: 'Validée', bg: 'rgba(3,105,161,0.12)', color: '#0369a1' },
    paid: { label: 'Payée', bg: 'rgba(22,163,74,0.12)', color: '#16a34a' },
    canceled: { label: 'Annulée', bg: 'rgba(75,85,99,0.12)', color: '#6b7280' },
};

function formatEUR(n) {
    return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0) + ' €';
}

function CreateCommissionDialog({ open, onClose, onCreated, grids, canPickAgent, agents }) {
    const [form, setForm] = useState({
        agent_id: '', mandate_id: '', property_address: '',
        ca_ht: '', tva_pct: 20, grid_id: '', agent_pct: 40, agency_pct: 60,
        bonus_flat: 0, signed_at: new Date().toISOString().slice(0, 10),
        source: 'manual', notes: '',
    });
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);

    const computePreview = useCallback(async () => {
        const ca = Number(form.ca_ht);
        if (!ca || ca <= 0) { setPreview(null); return; }
        try {
            const r = await api.post('/commissions/preview', {
                ...form, ca_ht: ca, tva_pct: Number(form.tva_pct),
                agent_pct: Number(form.agent_pct),
                agency_pct: Number(form.agency_pct),
                bonus_flat: Number(form.bonus_flat),
                grid_id: form.grid_id || null,
                agent_id: form.agent_id || null,
                mandate_id: form.mandate_id || null,
            });
            setPreview(r.data);
        } catch (e) { /* silencieux */ }
    }, [form]);

    useEffect(() => {
        if (!open) return;
        const t = setTimeout(computePreview, 300);
        return () => clearTimeout(t);
    }, [open, computePreview]);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (!form.ca_ht || Number(form.ca_ht) <= 0) {
            toast.error('CA HT requis');
            return;
        }
        setSaving(true);
        try {
            await api.post('/commissions', {
                ...form,
                ca_ht: Number(form.ca_ht),
                tva_pct: Number(form.tva_pct),
                agent_pct: Number(form.agent_pct),
                agency_pct: Number(form.agency_pct),
                bonus_flat: Number(form.bonus_flat),
                grid_id: form.grid_id || null,
                agent_id: form.agent_id || null,
                mandate_id: form.mandate_id || null,
                signed_at: form.signed_at ? new Date(form.signed_at).toISOString() : null,
            });
            toast.success('Commission enregistrée');
            onCreated();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Enregistrement impossible');
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
             data-testid="commission-create-dialog" onClick={onClose}>
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
                  className="w-full max-w-2xl rounded-2xl shadow-2xl p-5 space-y-3 max-h-[90vh] overflow-y-auto"
                  style={{ background: 'var(--card)', color: 'var(--foreground)' }}>
                <div>
                    <h3 className="text-lg font-bold">Nouvelle commission</h3>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                        Renseignez le CA HT et la grille (ou saisissez les % manuellement).
                    </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    {canPickAgent && (
                        <div className="col-span-2">
                            <label className="text-xs font-medium block mb-1">Agent</label>
                            <select value={form.agent_id}
                                    onChange={(e) => setForm({ ...form, agent_id: e.target.value })}
                                    data-testid="commission-input-agent"
                                    className="w-full px-3 py-2 rounded-lg border text-sm"
                                    style={{ borderColor: 'var(--border)' }}>
                                <option value="">Moi</option>
                                {agents.map((a) => (
                                    <option key={a.id} value={a.id}>{a.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="col-span-2">
                        <label className="text-xs font-medium block mb-1">Adresse du bien</label>
                        <input value={form.property_address}
                               onChange={(e) => setForm({ ...form, property_address: e.target.value })}
                               data-testid="commission-input-address"
                               className="w-full px-3 py-2 rounded-lg border text-sm"
                               style={{ borderColor: 'var(--border)' }} />
                    </div>
                    <div>
                        <label className="text-xs font-medium block mb-1">CA HT (€)</label>
                        <input type="number" step="1" value={form.ca_ht}
                               onChange={(e) => setForm({ ...form, ca_ht: e.target.value })}
                               data-testid="commission-input-ca"
                               className="w-full px-3 py-2 rounded-lg border text-sm"
                               style={{ borderColor: 'var(--border)' }} required />
                    </div>
                    <div>
                        <label className="text-xs font-medium block mb-1">Date de signature</label>
                        <input type="date" value={form.signed_at}
                               onChange={(e) => setForm({ ...form, signed_at: e.target.value })}
                               data-testid="commission-input-date"
                               className="w-full px-3 py-2 rounded-lg border text-sm"
                               style={{ borderColor: 'var(--border)' }} />
                    </div>
                    <div className="col-span-2">
                        <label className="text-xs font-medium block mb-1">Grille appliquée (optionnel)</label>
                        <select value={form.grid_id}
                                onChange={(e) => setForm({ ...form, grid_id: e.target.value })}
                                data-testid="commission-input-grid"
                                className="w-full px-3 py-2 rounded-lg border text-sm"
                                style={{ borderColor: 'var(--border)' }}>
                            <option value="">Aucune (saisie manuelle des %)</option>
                            {grids.filter((g) => g.is_active).map((g) => (
                                <option key={g.id} value={g.id}>{g.name}</option>
                            ))}
                        </select>
                    </div>
                    {!form.grid_id && (
                        <>
                            <div>
                                <label className="text-xs font-medium block mb-1">% agent</label>
                                <input type="number" step="0.5" value={form.agent_pct}
                                       onChange={(e) => setForm({ ...form, agent_pct: e.target.value })}
                                       data-testid="commission-input-agent-pct"
                                       className="w-full px-3 py-2 rounded-lg border text-sm"
                                       style={{ borderColor: 'var(--border)' }} />
                            </div>
                            <div>
                                <label className="text-xs font-medium block mb-1">% agence</label>
                                <input type="number" step="0.5" value={form.agency_pct}
                                       onChange={(e) => setForm({ ...form, agency_pct: e.target.value })}
                                       data-testid="commission-input-agency-pct"
                                       className="w-full px-3 py-2 rounded-lg border text-sm"
                                       style={{ borderColor: 'var(--border)' }} />
                            </div>
                        </>
                    )}
                </div>

                {preview && (
                    <div className="p-3 rounded-lg text-sm"
                         data-testid="commission-preview"
                         style={{ background: 'rgba(104,23,147,0.06)' }}>
                        <div className="flex items-center gap-1 mb-2 font-semibold"
                             style={{ color: '#681793' }}>
                            <Calculator className="w-4 h-4" /> Aperçu du calcul
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>Rémunération agent HT : <strong>{formatEUR(preview.agent_amount_ht)}</strong></div>
                            <div>Rémunération agence HT : <strong>{formatEUR(preview.agency_amount_ht)}</strong></div>
                            <div>TVA (20 %) : {formatEUR(preview.agent_tva)}</div>
                            <div>Agent TTC : <strong>{formatEUR(preview.agent_amount_ttc)}</strong></div>
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose}
                            className="text-sm px-3 py-2 rounded-lg"
                            style={{ color: 'var(--muted-foreground)' }}>
                        Annuler
                    </button>
                    <button type="submit" disabled={saving}
                            data-testid="commission-create-submit"
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold"
                            style={{ background: '#681793', color: '#fff' }}>
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        Enregistrer
                    </button>
                </div>
            </form>
        </div>
    );
}

export default function CommissionsPage() {
    const [user, setUser] = useState(null);
    const [scope, setScope] = useState('mine');
    const [year, setYear] = useState(new Date().getFullYear());
    const [summary, setSummary] = useState(null);
    const [list, setList] = useState([]);
    const [grids, setGrids] = useState([]);
    const [creating, setCreating] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const isAdmin = user && ['admin', 'super_admin', 'internal', 'group_manager'].includes(user.role);

    const updateBilling = async (commissionId, billing_status) => {
        try {
            await api.patch(`/commissions/${commissionId}/billing`, { billing_status });
            toast.success(billing_status === 'collected' ? 'Encaissement enregistré'
                : billing_status === 'invoiced' ? 'Facturation enregistrée' : 'Statut mis à jour');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de mise à jour');
        }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [meR, sumR, listR, gridR] = await Promise.all([
                api.get('/auth/me').catch(() => ({ data: user })),
                api.get(`/commissions/summary?scope=${scope}&year=${year}`),
                api.get(`/commissions?scope=${scope}&year=${year}&limit=200`),
                api.get('/commissions/grids').catch(() => ({ data: { items: [] } })),
            ]);
            setUser(meR.data);
            setSummary(sumR.data);
            setList(listR.data.items || []);
            setGrids(gridR.data.items || []);
        } catch (e) {
            if (e?.response?.status === 401) navigate('/login');
            else toast.error('Chargement impossible');
        } finally { setLoading(false); }
    }, [scope, year, navigate, user]);

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, year]);

    const totalAgent = summary?.total?.agent_ht || 0;
    const totalCa = summary?.total?.ca_ht || 0;

    const downloadCSV = () => {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/commissions/export/csv?scope=${scope}&year=${year}`;
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((blob) => {
                const u = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = u; a.download = `commissions-${year}.csv`;
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(u);
            })
            .catch(() => toast.error('Export impossible'));
    };

    const downloadBordereau = async (agentId) => {
        const now = new Date();
        const y = year;
        const m = now.getMonth() + 1;
        const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/commissions/bordereau/${agentId}/pdf?year=${y}&month=${m}`;
        try {
            const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            if (!r.ok) throw new Error('http');
            const blob = await r.blob();
            const u = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = u; a.download = `bordereau-${y}-${String(m).padStart(2, '0')}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(u);
        } catch { toast.error('Bordereau indisponible'); }
    };

    return (
        <ReseauLayout user={user} headerTitle="Rémunérations & commissions"
                      headerSubtitle="Suivi de vos commissions HT, TTC et bordereau PDF.">
            <main className="mx-auto px-4 sm:px-6 py-6 space-y-5"
                  data-testid="commissions-page"
                  style={{ maxWidth: 'var(--reseau-maxw-page)' }}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                        {isAdmin && (
                            <div className="inline-flex rounded-lg overflow-hidden border"
                                 data-testid="commissions-scope-toggle"
                                 style={{ borderColor: 'var(--border)' }}>
                                <button type="button" onClick={() => setScope('mine')}
                                        data-testid="commissions-scope-mine"
                                        className="px-3 py-1.5 text-sm"
                                        style={scope === 'mine'
                                            ? { background: '#681793', color: '#fff' }
                                            : { background: 'var(--card)' }}>
                                    Mes commissions
                                </button>
                                <button type="button" onClick={() => setScope('agency')}
                                        data-testid="commissions-scope-agency"
                                        className="px-3 py-1.5 text-sm border-l"
                                        style={scope === 'agency'
                                            ? { background: '#681793', color: '#fff' }
                                            : { background: 'var(--card)' }}>
                                    Agence
                                </button>
                            </div>
                        )}
                        <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                                data-testid="commissions-year"
                                className="text-sm px-3 py-1.5 rounded-lg border"
                                style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
                            {[0, 1, 2].map((d) => {
                                const y = new Date().getFullYear() - d;
                                return <option key={y} value={y}>{y}</option>;
                            })}
                        </select>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button type="button" onClick={downloadCSV}
                                data-testid="commissions-export-csv"
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border"
                                style={{ borderColor: 'rgba(104,23,147,0.35)', color: '#681793' }}>
                            <Download className="w-4 h-4" /> CSV {year}
                        </button>
                        <button type="button" onClick={() => downloadBordereau(user?.id)}
                                data-testid="commissions-export-bordereau"
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border"
                                style={{ borderColor: 'rgba(104,23,147,0.35)', color: '#681793' }}>
                            <FileText className="w-4 h-4" /> Bordereau
                        </button>
                        {isAdmin && (
                            <Link to="/agence/commissions/grilles"
                                  data-testid="commissions-grids-link"
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border"
                                  style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                                <Settings2 className="w-4 h-4" /> Grilles
                            </Link>
                        )}
                        <button type="button" onClick={() => setCreating(true)}
                                data-testid="commissions-create-btn"
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold"
                                style={{ background: '#681793', color: '#fff' }}>
                            <Plus className="w-4 h-4" /> Nouvelle
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-10">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto"
                                  style={{ color: '#681793' }} />
                    </div>
                ) : (
                    <>
                        <section className="grid sm:grid-cols-3 gap-3"
                                 data-testid="commissions-summary">
                            <div className="p-4 rounded-xl"
                                 style={{ background: 'var(--card)', border: '1px solid rgba(0,0,0,0.06)' }}>
                                <p className="text-[11px] font-semibold uppercase tracking-wider"
                                   style={{ color: 'var(--muted-foreground)' }}>
                                    CA HT généré · {year}
                                </p>
                                <p className="text-2xl font-extrabold tabular-nums mt-1"
                                   data-testid="commissions-total-ca">
                                    {formatEUR(totalCa)}
                                </p>
                            </div>
                            <div className="p-4 rounded-xl"
                                 style={{ background: 'linear-gradient(135deg, rgba(104,23,147,0.10) 0%, rgba(255,145,77,0.10) 100%)',
                                          border: '1px solid rgba(104,23,147,0.25)' }}>
                                <p className="text-[11px] font-semibold uppercase tracking-wider"
                                   style={{ color: '#681793' }}>
                                    Rémunération {scope === 'mine' ? 'agent' : 'agents'} HT · {year}
                                </p>
                                <p className="text-2xl font-extrabold tabular-nums mt-1"
                                   data-testid="commissions-total-agent">
                                    {formatEUR(totalAgent)}
                                </p>
                            </div>
                            <div className="p-4 rounded-xl"
                                 style={{ background: 'var(--card)', border: '1px solid rgba(0,0,0,0.06)' }}>
                                <p className="text-[11px] font-semibold uppercase tracking-wider"
                                   style={{ color: 'var(--muted-foreground)' }}>
                                    Nombre de dossiers
                                </p>
                                <p className="text-2xl font-extrabold tabular-nums mt-1">
                                    {summary?.total?.count || 0}
                                </p>
                            </div>
                        </section>

                        {scope === 'agency' && summary?.per_agent?.length > 0 && (
                            <section className="reseau-card p-4"
                                     data-testid="commissions-per-agent"
                                     style={{ background: 'var(--card)', border: '1px solid rgba(0,0,0,0.06)', borderRadius: '1rem' }}>
                                <h2 className="text-sm font-semibold uppercase tracking-wider mb-3"
                                    style={{ color: 'var(--muted-foreground)' }}>
                                    Top agents · {year}
                                </h2>
                                <ul className="space-y-1.5">
                                    {summary.per_agent.slice(0, 10).map((a) => (
                                        <li key={a.agent_id}
                                            data-testid={`agent-row-${a.agent_id}`}
                                            className="flex items-center justify-between gap-2 text-sm">
                                            <span className="font-medium">{a.agent_name}</span>
                                            <div className="flex items-center gap-3">
                                                <span style={{ color: 'var(--muted-foreground)' }}>{a.count} dossiers</span>
                                                <span className="tabular-nums font-semibold">{formatEUR(a.agent_ht)}</span>
                                                <button type="button"
                                                        onClick={() => downloadBordereau(a.agent_id)}
                                                        data-testid={`bordereau-${a.agent_id}`}
                                                        className="p-1 rounded"
                                                        style={{ color: '#681793' }}>
                                                    <FileText className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        <section data-testid="commissions-list">
                            <h2 className="text-sm font-semibold uppercase tracking-wider mb-2"
                                style={{ color: 'var(--muted-foreground)' }}>
                                Historique
                            </h2>
                            {list.length === 0 ? (
                                <div className="p-6 rounded-xl text-center"
                                     data-testid="commissions-empty"
                                     style={{ background: 'var(--card)', border: '1px solid rgba(0,0,0,0.06)' }}>
                                    <Euro className="w-8 h-8 mx-auto mb-2"
                                          style={{ color: 'rgba(0,0,0,0.25)' }} />
                                    <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                                        Aucune commission enregistrée sur {year}.
                                    </p>
                                </div>
                            ) : (
                                <ul className="space-y-2">
                                    {list.map((c) => {
                                        const s = STATUS_COLORS[c.status] || STATUS_COLORS.pending;
                                        return (
                                            <li key={c.id}
                                                data-testid={`commission-row-${c.id}`}
                                                className="p-3 rounded-lg border flex items-center justify-between gap-3 flex-wrap"
                                                style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <p className="text-sm font-medium truncate">
                                                            {c.property_address || 'Sans adresse'}
                                                        </p>
                                                        <span className="text-[10px] px-2 py-0.5 rounded-full"
                                                              style={{ background: s.bg, color: s.color }}>
                                                            {s.label}
                                                        </span>
                                                    </div>
                                                    <p className="text-[11px]" style={{ color: 'var(--muted-foreground)' }}>
                                                        {(c.signed_at || '').slice(0, 10)} · CA HT {formatEUR(c.ca_ht)}
                                                        {' · '}{c.agent_pct}% agent
                                                        {c.bonus_flat > 0 && ` · bonus ${formatEUR(c.bonus_flat)}`}
                                                    </p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm font-bold tabular-nums"
                                                       style={{ color: '#681793' }}>
                                                        {formatEUR(c.agent_amount_ht)}
                                                    </p>
                                                    <p className="text-[10px]"
                                                       style={{ color: 'var(--muted-foreground)' }}>
                                                        HT · TTC {formatEUR(c.agent_amount_ttc)}
                                                    </p>
                                                </div>
                                                {isAdmin && (
                                                    <select
                                                        value={c.billing_status || 'pending'}
                                                        onChange={(e) => updateBilling(c.id, e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        data-testid={`billing-select-${c.id}`}
                                                        title={`Facturé : ${(c.invoice_issued_at || '—').slice(0, 10)} · Encaissé : ${(c.collected_at || '—').slice(0, 10)}`}
                                                        className="text-[11px] rounded-lg border px-2 py-1.5 cursor-pointer"
                                                        style={{
                                                            borderColor: 'var(--border)',
                                                            background: c.billing_status === 'collected'
                                                                ? 'rgba(22,163,74,0.12)'
                                                                : c.billing_status === 'invoiced'
                                                                    ? 'rgba(3,105,161,0.12)'
                                                                    : 'var(--card)',
                                                            color: c.billing_status === 'collected'
                                                                ? '#16a34a'
                                                                : c.billing_status === 'invoiced' ? '#0369a1' : 'inherit',
                                                        }}
                                                    >
                                                        <option value="pending">À facturer</option>
                                                        <option value="invoiced">Facturée</option>
                                                        <option value="collected">Encaissée</option>
                                                        <option value="cancelled">Annulée</option>
                                                    </select>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </section>
                    </>
                )}

                <CreateCommissionDialog
                    open={creating}
                    onClose={() => setCreating(false)}
                    onCreated={() => { setCreating(false); load(); }}
                    grids={grids}
                    canPickAgent={isAdmin && scope === 'agency'}
                    agents={(summary?.per_agent || []).map((a) => ({ id: a.agent_id, name: a.agent_name }))}
                />
            </main>
        </ReseauLayout>
    );
}
